import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchUsersForCase,
  searchCases,
  fetchTaskTemplatesByService,
} from '../../services/TaskService';
import { searchClientsBasic } from '../../services/contractGeneratorService';
import { putPjudMonitoringClientOption } from 'services/detectionMonitoringServices';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { tasksI18n } from './i18n';

const ManageTaskModal = ({
  DesignSystemComponents,
  show,
  onClose,
  onSave,
  taskTitles = [],
  estados = [],
  prioridades = [],
  causasIniciales = [],
  clientes = [],
  usuarios = [],
  documentosAdjuntos = [],
  currentUserId,
  toEdit,
  defaultTaskType = null,
  preloadedClient = null,
  preloadedDescription = null,
  fromMonitoring = false,
  monitoringId = null,
  disableClientSelection = false,
  useInitialCases = false,
  defaultCauseSearchMode = null,
  disableCauseSearchMode = false,
  disableCaseSelection = false,
  isCreating = false,
}) => {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString().split('T')[0]; // YYYY-MM-DD
  }, []);

  const { Modal, Input, Dropdown, ComboBox } = DesignSystemComponents;

  const { language } = useLanguage();
  const tasksLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: tasksI18n });

  useEffect(() => {
    tasksI18n.changeLanguage(tasksLanguage);
  }, [tasksLanguage]);

  /* =============================
        FORM DATA
  ============================= */
  const [formData, setFormData] = useState({
    titulo: null,
    descripcion: '',
    estado: null,
    prioridad: null,
    causa: null,
    asignadoA: null,
    fechaVencimiento: '',
    documentoAdjunto: null,
  });

  const [assignmentTarget, setAssignmentTarget] = useState('causa');
  const [causeSearchMode, setCauseSearchMode] = useState('cliente');
  const [selectedClientForCausa, setSelectedClientForCausa] = useState(null);
  const [isLoadingAsignados, setIsLoadingAsignados] = useState(false);
  const [asignadoAOptions, setAsignadoAOptions] = useState(usuarios);
  const [clientSearchText, setClientSearchText] = useState('');
  const [clientOptions, setClientOptions] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [caseSearchText, setCaseSearchText] = useState('');
  const [caseOptions, setCaseOptions] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);
  const [comboKey, setComboKey] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState('');

  /* =============================
        PLANTILLAS
  ============================= */
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [extraParams, setExtraParams] = useState([]);

  const taskTitleOptions = useMemo(
    () => (Array.isArray(taskTitles) ? taskTitles : []),
    [taskTitles]
  );

  const normalizeText = (value) =>
    String(value || '')
      .trim()
      .toLowerCase();

  const resolveTaskTitleOption = (rawValue) => {
    if (!rawValue) return null;

    const asObject = typeof rawValue === 'object' ? rawValue : null;
    const rawBackendId = Number(
      asObject?.backendId ?? asObject?.id ?? asObject?.value ?? 0
    );

    if (rawBackendId > 0) {
      const byId = taskTitleOptions.find((option) => {
        const optionId = Number(
          option?.backendId ?? option?.id ?? option?.value ?? 0
        );
        return optionId === rawBackendId;
      });

      if (byId) return byId;
      if (asObject?.label) {
        const byLabel = taskTitleOptions.find(
          (option) =>
            normalizeText(option?.label) === normalizeText(asObject.label)
        );
        if (byLabel) return byLabel;
      }
    }

    const rawLabel =
      typeof rawValue === 'string'
        ? rawValue
        : (asObject?.label ?? asObject?.code ?? '');

    if (!rawLabel) return null;

    return (
      taskTitleOptions.find((option) => {
        const label = normalizeText(option?.label);
        const value = normalizeText(option?.value);
        const code = normalizeText(option?.code);
        const target = normalizeText(rawLabel);
        return label === target || value === target || code === target;
      }) || null
    );
  };

  const selectedTaskTitleOption = useMemo(
    () => resolveTaskTitleOption(formData.titulo),
    [formData.titulo, taskTitleOptions]
  );
  const titleComboValue = useMemo(() => {
    if (selectedTaskTitleOption) return selectedTaskTitleOption;
    if (!formData.titulo) return null;

    if (typeof formData.titulo === 'string') {
      return {
        label: formData.titulo,
        value: formData.titulo,
      };
    }

    if (typeof formData.titulo === 'object') {
      const label = formData.titulo.label ?? formData.titulo.value ?? '';
      if (!label) return null;

      return {
        ...formData.titulo,
        label,
        value: formData.titulo.value ?? label,
      };
    }

    return null;
  }, [formData.titulo, selectedTaskTitleOption]);
  const hasInvalidTaskTitle = useMemo(
    () => Boolean(formData.titulo) && !selectedTaskTitleOption,
    [formData.titulo, selectedTaskTitleOption]
  );

  const estadoOptions = useMemo(
    () => (Array.isArray(estados) ? estados : []),
    [estados]
  );
  const prioridadOptions = useMemo(
    () => (Array.isArray(prioridades) ? prioridades : []),
    [prioridades]
  );
  const causaInicialOptions = useMemo(() => {
    if (!Array.isArray(causasIniciales)) return [];
    return causasIniciales
      .map((c) => {
        if (!c) return null;
        const backendId = c.backendId ?? c.caseId ?? c.id ?? c.value ?? null;
        if (!backendId) return null;
        return {
          ...c,
          backendId,
          value: c.value ?? c.caseId ?? c.id ?? backendId,
          label:
            c.label ??
            c.title ??
            c.caseTitle ??
            `${c.code || c.caseCode || 'SIN-ROL'} — ${
              c.title || c.caseTitle || ''
            }`.trim(),
        };
      })
      .filter(Boolean);
  }, [causasIniciales]);

  const filterActiveUsers = (list) =>
    (Array.isArray(list) ? list : []).filter((user) => user.active === true);

  const usuarioOptions = useMemo(() => filterActiveUsers(usuarios), [usuarios]);

  const documentosOptions = useMemo(
    () => (Array.isArray(documentosAdjuntos) ? documentosAdjuntos : []),
    [documentosAdjuntos]
  );

  /* =============================
        INICIALIZAR MODAL
  ============================= */
  useEffect(() => {
    if (!show) return;

    const initialAssignmentTarget = defaultTaskType
      ? defaultTaskType.toLowerCase()
      : 'causa';
    setAssignmentTarget(initialAssignmentTarget);
    setCauseSearchMode(defaultCauseSearchMode || 'cliente');

    let initialSelectedClient = null;
    let initialDescription = '';
    let initialCausa = null;

    if (preloadedClient) {
      initialSelectedClient = {
        label: preloadedClient.name,
        value: preloadedClient.id,
        backendId: preloadedClient.id,
        rut: preloadedClient.rut || null,
        clientId: preloadedClient.clientId || null,
      };
      initialDescription = preloadedDescription || '';
    }

    if (causaInicialOptions.length === 1) {
      initialCausa = causaInicialOptions[0];
    }

    setSelectedClientForCausa(initialSelectedClient);
    setClientOptions([]);
    setCaseOptions(useInitialCases ? causaInicialOptions : []);
    setAsignadoAOptions(usuarioOptions);

    setFormData({
      titulo: null,
      descripcion: initialDescription,
      estado: null,
      prioridad: null,
      asignadoA: null,
      fechaVencimiento: '',
      documentoAdjunto: null,
      causa: initialCausa,
    });

    setComboKey((k) => k + 1);
    setTemplates([]);
    setSelectedTemplate(null);
    setExtraParams([]);
    setFormError('');
  }, [
    show,
    usuarioOptions,
    defaultTaskType,
    defaultCauseSearchMode,
    preloadedClient,
    preloadedDescription,
    causaInicialOptions,
    useInitialCases,
  ]);

  /* =============================
        Cargar abogados según causa
  ============================= */
  useEffect(() => {
    const load = async () => {
      if (!formData.causa || !formData.causa.backendId) {
        setAsignadoAOptions(usuarioOptions);
        return;
      }

      setIsLoadingAsignados(true);
      try {
        const arr = await fetchUsersForCase(formData.causa.backendId);
        const activeArr = filterActiveUsers(arr);
        if (activeArr.length > 0) {
          setAsignadoAOptions(activeArr);
          setFormData((prev) => ({ ...prev, asignadoA: activeArr[0] }));
        } else {
          setAsignadoAOptions(usuarioOptions);
        }
      } catch {
        setAsignadoAOptions(usuarioOptions);
      } finally {
        setIsLoadingAsignados(false);
      }
    };

    load();
  }, [formData.causa, usuarioOptions]);

  /* =============================
        BUSCAR CLIENTES
  ============================= */
  const fetchClients = async (text) => {
    if (!text || text.length < 3) {
      setClientOptions([]);
      return;
    }

    setLoadingClients(true);

    try {
      const result = await searchClientsBasic({ text });

      setClientOptions(
        result.map((c) => ({
          label: `${c.name} (${c.rut})`,
          value: c.id,
          backendId: c.id,
          rut: c.rut,
          clientId: c.clientId,
        }))
      );
    } catch {
      setClientOptions([]);
    } finally {
      setLoadingClients(false);
    }
  };

  useEffect(() => {
    const deb = setTimeout(() => fetchClients(clientSearchText), 400);
    return () => clearTimeout(deb);
  }, [clientSearchText]);

  /* =============================
        BUSCAR CAUSAS DINÁMICO
  ============================= */
  /* =============================
        BUSCAR CAUSAS DINÁMICO
  ============================= */
  const loadCases = async () => {
    if (useInitialCases && !caseSearchText && causaInicialOptions.length > 0) {
      setCaseOptions(causaInicialOptions);
      setLoadingCases(false);
      return;
    }

    setLoadingCases(true);

    const groupId =
      assignmentTarget === 'sac' ? 2 : assignmentTarget === 'eepp' ? 11 : null;

    const clientId =
      causeSearchMode === 'cliente' && selectedClientForCausa
        ? selectedClientForCausa.clientId || selectedClientForCausa.value
        : null;

    try {
      const result = await searchCases({
        groupId,
        clientId,
        search: caseSearchText,
      });

      setCaseOptions(result);
    } catch {
      setCaseOptions([]);
    } finally {
      setLoadingCases(false);
    }
  };

  useEffect(() => {
    const deb = setTimeout(() => loadCases(), 400);
    return () => clearTimeout(deb);
  }, [
    caseSearchText,
    selectedClientForCausa,
    assignmentTarget,
    useInitialCases,
    causaInicialOptions,
  ]);

  useEffect(() => {
    // Solo aplicable cuando asignamos por SAC/EEPP
    if (assignmentTarget === 'causa') return;
    if (!selectedClientForCausa) return;
    if (!caseOptions || caseOptions.length !== 1) return;
    if (formData.causa) return;

    setFormData((prev) => ({
      ...prev,
      causa: caseOptions[0],
    }));
  }, [assignmentTarget, selectedClientForCausa, caseOptions, formData.causa]);

  /* =============================
        VALIDACIÓN
  ============================= */
  const isValid = useMemo(() => {
    const hasValidTitle =
      !!selectedTaskTitleOption &&
      Number(
        selectedTaskTitleOption.backendId || selectedTaskTitleOption.value
      ) > 0;

    const base =
      hasValidTitle &&
      formData.descripcion &&
      formData.estado &&
      formData.prioridad &&
      formData.fechaVencimiento;

    if (assignmentTarget === 'causa') {
      return base && formData.causa && formData.asignadoA;
    }

    if (assignmentTarget === 'sac' || assignmentTarget === 'eepp') {
      return base && formData.causa && formData.asignadoA;
    }

    return base;
  }, [formData, assignmentTarget, selectedTaskTitleOption]);

  const handleFieldChange = (field) => (value) => {
    if (field === 'titulo') {
      setFormError('');
    }
    const newValue = value?.target?.value ?? value;
    setFormData((prev) => ({ ...prev, [field]: newValue }));
  };

  /* =============================
        CARGAR PLANTILLAS (CORREGIDO)
  ============================= */
  useEffect(() => {
    if (!selectedTaskTitleOption || !selectedTaskTitleOption.serviceId) {
      setTemplates([]);
      setSelectedTemplate(null);
      setExtraParams([]);
      return;
    }

    const loadTemplates = async () => {
      try {
        const serviceId = selectedTaskTitleOption.serviceId;
        const taskConfiguratorId = selectedTaskTitleOption.backendId ?? null;

        const list = await fetchTaskTemplatesByService(
          serviceId,
          taskConfiguratorId
        );

        setTemplates(list);
      } catch (e) {
        console.error('Error al cargar plantillas:', e);
      }
    };

    loadTemplates();
  }, [selectedTaskTitleOption]);

  const handleTemplateChange = (tpl) => {
    setSelectedTemplate(tpl);
    if (tpl?.extraFields?.length > 0) {
      setExtraParams(
        tpl.extraFields.map((f) => ({
          name: f.name,
          value: f.id,
        }))
      );
    } else {
      setExtraParams([]);
    }
  };

  /* =============================
        GUARDAR
  ============================= */
  const handleSave = async () => {
    if (isSaving) return;

    const selectedTaskConfiguratorId = Number(
      selectedTaskTitleOption?.backendId ?? selectedTaskTitleOption?.value ?? 0
    );

    if (!selectedTaskTitleOption || selectedTaskConfiguratorId <= 0) {
      setFormError(t('modal_error_invalid_title'));
      return;
    }

    if (!isValid) return;

    setIsSaving(true);
    setFormError('');

    let saveSuccessful = false;

    const payload = {
      name: selectedTaskTitleOption.label || t('modal_payload_untitled'),
      description: formData.descripcion || '',
      statusId: formData.estado?.value || 1,
      priorityId: formData.prioridad?.value,
      deadlineTime: formData.fechaVencimiento
        ? new Date(formData.fechaVencimiento).toISOString()
        : null,
      caseId: formData.causa?.backendId || null,
      creatorId: currentUserId,
      assignedToId: formData.asignadoA?.value || null,
      taskConfiguratorId: selectedTaskConfiguratorId,
      templateId: selectedTemplate?.value || null,
      extraParams,
    };

    try {
      await onSave?.(payload);
      saveSuccessful = true;
    } catch (error) {
      console.error('Error al guardar la tarea:', error);
      setIsSaving(false);
      return;
    }

    if (saveSuccessful && fromMonitoring && monitoringId && !toEdit) {
      try {
        await putPjudMonitoringClientOption.putMonitoringOption(
          monitoringId,
          11,
          currentUserId
        );
      } catch (error) {
        console.error('Error al actualizar monitoreo:', error);
      }
    }

    setIsSaving(false);
    onClose();
  };

  const isAssignmentTargetDisabled = !!defaultTaskType;

  const isClientDisabled =
    disableClientSelection || (!!defaultTaskType && !!preloadedClient);

  /* =============================
        UI DEL MODAL COMPLETO
  ============================= */
  return (
    <Modal
      title={toEdit ? t('modal_title_edit') : t('modal_title_create')}
      show={show}
      onClose={onClose}
      primaryBtnTxt={
        isCreating
          ? t('modal_btn_creating')
          : toEdit
            ? t('modal_btn_edit')
            : t('modal_btn_create')
      }
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid || isCreating}
      secondaryBtnTxt={t('modal_btn_cancel')}
      secondaryBtnAction={onClose}
      width='900px'
    >
      <div className='row g-3'>
        {/* TIPO */}
        <div className='col-md-4'>
          <Dropdown
            label={t('modal_field_type')}
            required
            value={{
              label:
                assignmentTarget === 'causa'
                  ? t('modal_type_causa')
                  : assignmentTarget === 'sac'
                    ? t('modal_type_sac')
                    : t('modal_type_eepp'),
              value: assignmentTarget,
            }}
            onChange={(opt) => {
              const value = opt?.value || 'causa';

              setAssignmentTarget(value);
              setComboKey((k) => k + 1);
              setSelectedClientForCausa(null);
              if (value === 'sac') {
                const sacUsers = usuarioOptions.filter((x) => x.roleId === 5);
                setAsignadoAOptions(sacUsers);
              } else {
                // Para causa / eepp: sin filtro (o puedes poner otro criterio)
                setAsignadoAOptions(usuarioOptions);
              }
              setClientSearchText('');
              setCaseSearchText('');
              setClientOptions([]);
              setCaseOptions([]);
              setFormData((prev) => ({ ...prev, causa: null }));
            }}
            options={[
              { label: t('modal_type_causa'), value: 'causa' },
              { label: t('modal_type_sac'), value: 'sac' },
              { label: t('modal_type_eepp'), value: 'eepp' },
            ]}
            disabled={isAssignmentTargetDisabled}
            className='space-like-input'
          />
        </div>

        {/* =============================
            BUSCADORES
        ============================= */}
        {assignmentTarget === 'causa' ? (
          <div className='col-md-8'>
            <div className='d-flex gap-3'>
              <div style={{ width: '220px' }}>
                <Dropdown
                  label={t('modal_field_search_by')}
                  value={
                    causeSearchMode === 'cliente'
                      ? { label: t('modal_search_client'), value: 'cliente' }
                      : { label: t('modal_search_causa'), value: 'causa' }
                  }
                  onChange={(opt) => {
                    const value = opt?.value || 'cliente';

                    setCauseSearchMode(value);
                    setComboKey((k) => k + 1);
                    setSelectedClientForCausa(null);
                    setClientSearchText('');
                    setCaseSearchText('');
                    setClientOptions([]);
                    setCaseOptions([]);
                    setFormData((prev) => ({ ...prev, causa: null }));
                  }}
                  options={[
                    { label: t('modal_search_client'), value: 'cliente' },
                    { label: t('modal_search_causa'), value: 'causa' },
                  ]}
                  isDisabled={isClientDisabled || disableCauseSearchMode}
                  disabled={isClientDisabled || disableCauseSearchMode}
                  className='space-like-input'
                />
              </div>

              <div className='flex-grow-1'>
                {causeSearchMode === 'cliente' ? (
                  <ComboBox
                    key={comboKey}
                    label={t('modal_label_client')}
                    placeholder={t('modal_placeholder_client')}
                    noResultText={t('modal_no_results')}
                    value={selectedClientForCausa}
                    onChange={(v) => {
                      setSelectedClientForCausa(v);
                    }}
                    onInputChange={(text) => setClientSearchText(text)}
                    options={clientOptions}
                    isLoading={loadingClients}
                    disabled={isClientDisabled}
                    className='space-like-input'
                  />
                ) : (
                  <ComboBox
                    key={comboKey}
                    label={t('modal_label_causa')}
                    placeholder={t('modal_placeholder_causa')}
                    noResultText={t('modal_no_results')}
                    value={formData.causa}
                    onChange={handleFieldChange('causa')}
                    onInputChange={(text) => setCaseSearchText(text)}
                    options={caseOptions}
                    isLoading={loadingCases}
                    className='space-like-input'
                  />
                )}
              </div>
            </div>

            {causeSearchMode === 'cliente' && selectedClientForCausa && (
              <div>
                <ComboBox
                  key={comboKey + 1}
                  label={t('modal_label_causa')}
                  placeholder={t('modal_placeholder_causa')}
                  noResultText={t('modal_no_results')}
                  required
                  value={formData.causa}
                  onChange={handleFieldChange('causa')}
                  options={caseOptions}
                  isLoading={loadingCases}
                  disabled={disableCaseSelection}
                  isDisabled={disableCaseSelection}
                  className='space-like-input'
                />
              </div>
            )}
          </div>
        ) : (
          <div className='col-md-8'>
            <div className='d-flex gap-3'>
              <div className='flex-grow-1'>
                <ComboBox
                  disabled={isClientDisabled}
                  key={comboKey}
                  label={t('modal_label_client')}
                  placeholder={t('modal_placeholder_client')}
                  noResultText={t('modal_no_results')}
                  value={selectedClientForCausa}
                  onChange={(v) => {
                    if (!isClientDisabled) {
                      setSelectedClientForCausa(v);
                      setCaseSearchText('');
                      setCaseOptions([]);
                      setFormData((prev) => ({ ...prev, causa: null }));
                    } else if (v === null) {
                      if (!!preloadedClient) {
                        setSelectedClientForCausa({
                          label: preloadedClient.name,
                          value: preloadedClient.id,
                          backendId: preloadedClient.id,
                          rut: preloadedClient.rut || null,
                        });
                      } else {
                        setSelectedClientForCausa(null);
                      }
                    }
                  }}
                  onInputChange={(text) => setClientSearchText(text)}
                  options={clientOptions}
                  isLoading={loadingClients}
                  className='space-like-input'
                />
              </div>
            </div>
            {selectedClientForCausa && (
              <div>
                <ComboBox
                  key={comboKey + 1}
                  label={t('modal_label_causa')}
                  required
                  placeholder={t('modal_placeholder_causa_select')}
                  noResultText={t('modal_no_results')}
                  value={formData.causa}
                  onChange={handleFieldChange('causa')}
                  onInputChange={(text) => setCaseSearchText(text)}
                  options={caseOptions}
                  isLoading={loadingCases}
                  disabled={disableCaseSelection}
                  isDisabled={disableCaseSelection}
                  className='space-like-input'
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Nomenclatura */}
      <div className='row'>
        <div className='col-12'>
          <ComboBox
            label={t('modal_label_nomenclature')}
            placeholder={t('modal_placeholder_nomenclature')}
            noResultText={t('modal_no_results')}
            required
            value={titleComboValue}
            onChange={handleFieldChange('titulo')}
            onInputChange={handleFieldChange('titulo')}
            options={taskTitleOptions}
            className='space-like-input'
          />
          {(hasInvalidTaskTitle || formError) && (
            <small className='text-danger d-block mt-1'>
              {formError || t('modal_error_invalid_nomenclature')}
            </small>
          )}
        </div>
      </div>
      <div className='row'>
        {/* Descripción */}
        <div className='col-12'>
          <Input
            label={t('modal_label_description')}
            multiline
            rows={3}
            value={formData.descripcion}
            onChange={handleFieldChange('descripcion')}
          />
        </div>
      </div>
      <div className='row'>
        {/* Estado */}
        <div className='col-md-6'>
          <ComboBox
            label={t('modal_label_status')}
            value={formData.estado}
            onChange={handleFieldChange('estado')}
            options={estadoOptions}
            placeholder={t('modal_placeholder_select')}
            noResultText={t('modal_no_results')}
            className='space-like-input'
          />
        </div>

        {/* Prioridad */}
        <div className='col-md-6'>
          <ComboBox
            label={t('modal_label_priority')}
            value={formData.prioridad}
            onChange={handleFieldChange('prioridad')}
            options={prioridadOptions}
            placeholder={t('modal_placeholder_select')}
            noResultText={t('modal_no_results')}
            className='space-like-input'
          />
        </div>
      </div>
      <div className='row'>
        {/* Asignado */}
        <div className='col-md-6'>
          <ComboBox
            label={t('modal_label_assigned')}
            value={formData.asignadoA}
            onChange={handleFieldChange('asignadoA')}
            noResultText={t('modal_no_results')}
            options={
              assignmentTarget?.toLowerCase() === 'sac'
                ? asignadoAOptions.filter((u) => Number(u.roleId) === 5)
                : asignadoAOptions
            }
            isLoading={isLoadingAsignados}
            placeholder={t('modal_placeholder_select')}
            className='space-like-input'
          />
        </div>

        {/* Fecha */}
        <div className='col-md-6'>
          <Input
            type='date'
            label={t('modal_label_due_date')}
            value={formData.fechaVencimiento}
            onChange={handleFieldChange('fechaVencimiento')}
            min={today}
          />
        </div>
      </div>

      {/* Documento adjunto
      <div className='row'>
        <div className='col-12'>
          <Dropdown
            label='Documento adjunto'
            value={formData.name}
            onChange={handleFieldChange('documentoAdjunto')}
            options={documentosOptions}
          />
        </div> 
      </div> */}

      {/* PLANTILLAS */}
      <div className='col-12'>
        <ComboBox
          label={t('modal_label_document')}
          noResultText={t('modal_no_results')}
          placeholder={
            templates.length === 0
              ? t('modal_placeholder_no_documents')
              : t('modal_placeholder_select_document')
          }
          value={selectedTemplate}
          onChange={handleTemplateChange}
          options={templates} // si está vacío, simplemente no mostrará opciones
          isDisabled={templates.length === 0}
          className='space-like-input'
        />
      </div>

      {extraParams.length > 0 && (
        <div className='col-12 mt-3'>
          <h6>{t('modal_template_variables')}</h6>
          {extraParams.map((p) => (
            <Input
              key={p.name}
              label={p.name}
              value={p.value}
              onChange={(e) =>
                setExtraParams((prev) =>
                  prev.map((x) =>
                    x.name === p.name ? { ...x, value: e.target.value } : x
                  )
                )
              }
              className='mt-2'
            />
          ))}
        </div>
      )}
    </Modal>
  );
};

export default ManageTaskModal;
